import React, { useState } from "react"
import { LayoutChangeEvent, View } from "react-native"

import { useOnOff } from "@axtesys/hooks"

import { PressableOpacity } from "../../button/PressableOpacity"
import { DEFAULT_KEY } from "../../constants"
import { Key, KeyLabels, Menu } from "../../container/Menu"
import { useDisplayValue } from "../../hooks/useDisplayValue"
import { useReCalculationEffect } from "../../hooks/useReCalculationEffect"
import { calculatePosition } from "../../lib"
import { ScreenCoordinates } from "../../types"
import { TextInput, TextInputProps } from "./TextInput"

type DropdownInputProps = Omit<
  TextInputProps,
  "mode" | "iconLeft" | "iconRight" | "onChange"
> & {
  displayKeyValuePairs: KeyLabels

  excludedValues?: Key[]
}

type SingleDropdownInputProps = DropdownInputProps & {
  onChange(value: Key): void

  value?: Key
}

type MultiDropdownInputProps = DropdownInputProps & {
  values: Key[]
  category: string
  onChange(values: Key[]): void
}

type DropdownInputInternalProps = DropdownInputProps & {
  displayValue: string
  mode: "single" | "multi"
  onSelect(key: Key): void

  values?: Key[]
}

export { SingleDropdownInput, MultiDropdownInput }

function SingleDropdownInput(props: SingleDropdownInputProps) {
  const { value, displayKeyValuePairs, onChange } = props

  return (
    <InternalDropdownInput
      mode="single"
      displayValue={displayKeyValuePairs[value ?? ""] ?? ""}
      onSelect={onChange}
      {...props}
    />
  )
}

function MultiDropdownInput(props: MultiDropdownInputProps) {
  const { values, onChange } = props

  const onSelectMulti = (key: Key) => {
    if (!values.includes(key)) {
      if (
        key == DEFAULT_KEY ||
        (values.length == 1 && values[0] == DEFAULT_KEY)
      ) {
        onChange([key])
      } else onChange([...values, key])
    } else if (values.length > 1) {
      onChange(values.filter(value => value != key))
    } else onChange([DEFAULT_KEY])
  }

  return (
    <InternalDropdownInput
      mode="multi"
      displayValue={useDisplayValue(props)}
      onSelect={onSelectMulti}
      {...props}
    />
  )
}

function InternalDropdownInput(props: DropdownInputInternalProps) {
  const {
    mode,
    values,
    displayValue,
    excludedValues,
    displayKeyValuePairs,
    onSelect,
    ...textInputProps
  } = props
  const { disabled } = textInputProps

  // States whether the dropdown menu is shown or not.
  const [menuVisible, showMenu, hideMenu] = useOnOff(false)

  // Dimensions received from the `TextInput` component in order to:
  // Set the `minWidth` of the menu. This leads to a better fit /
  // display for a non-collapsed `Menu`.
  const [minWidth, setMinWidth] = useState<number | undefined>()

  // Required to adjust positioning
  // of dropdown when window dimensions change.
  const viewRef = React.createRef<View>()
  const [position, setPosition] = useState<ScreenCoordinates>({ x: 0, y: 0 })
  const calculateMenuPosition = () =>
    calculatePosition({ viewRef, setPosition, viewHeightAddition: true })

  useReCalculationEffect({
    dependency: menuVisible,
    executeIfNotVisible: hideMenu,
    calculate: calculateMenuPosition,
  })

  const selectOption = (key: Key) => {
    onSelect(key)
    calculateMenuPosition()
    if (mode == "single") hideMenu()
  }

  const onLayout = ({ nativeEvent: { layout } }: LayoutChangeEvent) =>
    setMinWidth(layout.width)

  const interceptor = (
    <PressableOpacity disabled={disabled} onPress={showMenu}>
      <View ref={viewRef} pointerEvents="none">
        <TextInput
          {...textInputProps}
          editable={false}
          value={displayValue}
          iconRight={{ name: menuVisible ? "chevron-up" : "chevron-down" }}
          onChange={() => {}}
        />
      </View>
    </PressableOpacity>
  )

  const menu = (
    <Menu
      minWidth={minWidth}
      position={position}
      visible={menuVisible}
      selectedValues={values}
      excludedValues={excludedValues}
      displayKeyValuePairs={displayKeyValuePairs}
      onDismiss={hideMenu}
      onSelect={selectOption}
    />
  )

  return (
    <View pointerEvents={disabled ? "none" : undefined} onLayout={onLayout}>
      {interceptor}
      {!disabled && menu}
    </View>
  )
}
